import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable, Observer, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { environment } from 'src/environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonArrayService } from 'src/app/core/services/common-array.service';

@Component({
  selector: 'app-banners',
  templateUrl: './banners.component.html',
  styleUrls: ['./banners.component.scss'],
})
export class BannersComponent implements OnInit {
  addUpdateBannerForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  imageLoading = false;
  pageSize = 10;
  pageIndex = 1;
  totalTagDataCount: number = 0;
  thumbImageLoading = false;
  checkAddPermission = false;
  checkUpdatePermission = false;
  mediaBaseUrl = environment.mediaBaseUrl;
  mediaUploadUrl = environment.apiBaseUrl + '/api/media';
  urlRegexPattern =
    /^https:\/\/(mockshala-user-web\.pages\.dev|mockshala\.com)/;

  slideConfig = {
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
  };
  bannerData: any = [];

  moduleList: any[] = [];

  slideModuleData: { [index: number]: any[] } = {};
  slideModuleLoading: { [index: number]: boolean } = {};
  slideModuleTotalCount: { [index: number]: number } = {};
  slideModuleSearch: { [index: number]: Subject<string> } = {};
  slideModulePageIndex: { [index: number]: number } = {};

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private msg: NzMessageService,
    private commonArrayService: CommonArrayService,
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params.id;
    this.addUpdateBannerForm = this.fb.group({
      slides: this.fb.array([]),
    });
    this.moduleList = this.commonArrayService.moduleList;
  }

  ngOnInit(): void {
    this.getBanners();
    if (this.slideFormArray.length < 1) {
      this.addSlides();
    }
  }

  createSlide(item?: any): FormGroup {
    if (item) {
      return this.fb.group({
        _id: [item._id],
        title: [item.title],
        position: [item.position, [Validators.required]],
        image: [item.image, [Validators.required]],
        imageForMobile: [item.imageForMobile, [Validators.required]],
        redirectUrl: [
          item.redirectUrl,
          [Validators.required, Validators.pattern(this.urlRegexPattern)],
        ],
        status: [item.status || 'active', [Validators.required]],

        module: [item.module || null, [Validators.required]],
        referenceId: [
          item.referenceId?._id || item.referenceId || null,
          [Validators.required],
        ],
      });
    }
    return this.fb.group({
      _id: [null],
      title: [null],
      position: [null, [Validators.required]],
      image: [null, [Validators.required]],
      imageForMobile: [null, [Validators.required]],
      redirectUrl: [
        null,
        [Validators.required, Validators.pattern(this.urlRegexPattern)],
      ],
      status: ['active', [Validators.required]],
      module: [null, [Validators.required]],
      referenceId: [null, [Validators.required]],
    });
  }

  get slideFormArray(): FormArray<any> {
    return this.addUpdateBannerForm.get('slides') as FormArray;
  }

  addSlides(): void {
    const newPosition = this.slideFormArray.length + 1;
    const newSlide = this.createSlide({ position: newPosition });
    this.slideFormArray.push(newSlide);
  }

  removeSlide(index: number, id?: any): void {
    if (id) {
      this.httpRequestService
        .request('delete', `banners/${id}`)
        .subscribe((result: any) => {
          if (result) {
            this.notificationService.success('', 'Banner Deleted Successfully');
            this.slideFormArray.removeAt(index);
            this.cleanupSlideModuleState(index);
            this.updateSlidePositions();
          }
        });
    } else {
      this.slideFormArray.removeAt(index);
      this.cleanupSlideModuleState(index);
      this.updateSlidePositions();
    }
  }

  private updateSlidePositions(): void {
    this.slideFormArray.controls.forEach((control, index) => {
      control.patchValue({ position: index + 1 });
    });
  }

  initSlideModuleSearch(index: number): void {
    if (!this.slideModuleSearch[index]) {
      this.slideModuleSearch[index] = new Subject<string>();
      this.slideModuleSearch[index]
        .pipe(debounceTime(800))
        .subscribe((search: string) => {
          this.slideModuleData[index] = [];
          this.slideModulePageIndex[index] = 1;
          this.fetchModuleData(index, search, 0);
        });
    }
  }

  /**
   * Called when the user picks a value from the "module" dropdown.
   * Clears referenceId and refetches items for the new module.
   */
  onModuleChange(module: string, index: number): void {
    this.slideFormArray.at(index).patchValue({ referenceId: null });
    this.slideModuleData[index] = [];
    this.slideModulePageIndex[index] = 1;
    this.slideModuleTotalCount[index] = 0;
    this.updateReferenceIdValidators(module, index);
    if (module && module !== 'freeMock') {
      this.initSlideModuleSearch(index);
      this.fetchModuleData(index, '', 0);
    }
  }

  /** freeMock has no reference item to pick — referenceId is optional for it. */
  private updateReferenceIdValidators(module: string, index: number): void {
    const referenceIdControl =
      this.slideFormArray.at(index).get('referenceId');
    if (module === 'freeMock') {
      referenceIdControl?.clearValidators();
    } else {
      referenceIdControl?.setValidators([Validators.required]);
    }
    referenceIdControl?.updateValueAndValidity();
  }

  /** Called by nzOnSearch on the referenceId nz-select. */
  onModuleSearch(searchText: string, index: number): void {
    this.initSlideModuleSearch(index);
    this.slideModuleSearch[index].next(searchText);
  }

  /** Infinite scroll — load next page of items. */
  loadMoreModuleData(index: number): void {
    const total = this.slideModuleTotalCount[index] || 0;
    const loaded = (this.slideModuleData[index] || []).length;
    if (total > loaded) {
      this.slideModulePageIndex[index] =
        (this.slideModulePageIndex[index] || 1) + 1;
      const skip =
        this.pageSize * ((this.slideModulePageIndex[index] || 1) - 1);
      this.fetchModuleData(index, '', skip);
    }
  }

  /**
   * Fetches items for the selected module from the API.
   * Each case maps to the schema enum value and its API endpoint.
   */
  fetchModuleData(index: number, search: string, skip: number): void {
    const module = this.slideFormArray.at(index).get('module')?.value;
    if (!module || module === 'freeMock') return;

    this.slideModuleLoading[index] = true;
    let url = '';
    let params: any = { skip, limit: this.pageSize };
    if (search) params.search = search;

    switch (module) {
      case 'plan':
        url = 'plans';
        params.status = true;
        params.approveStatus = 'approved';
        break;
      case 'testSery':
        url = 'test-series/for-list';
        params.approveStatus = 'approved';
        break;
      case 'editorial':
        url = 'editorials';
        params.status = true;
        params.approveStatus = 'approved';
        break;
      case 'currentAffair':
        url = 'current-affairs';
        params.status = true;
        params.approveStatus = 'approved';
        break;
      case 'jobAlert':
        url = 'job-alerts';
        params.status = true;
        params.approveStatus = 'approved';
        params.publishedStatus = 'published';
        break;
    }

    this.httpRequestService.request('get', url, params).subscribe(
      (result: any) => {
        this.slideModuleLoading[index] = false;
        const incoming = result.data || [];
        this.slideModuleData[index] =
          skip === 0
            ? incoming
            : [...(this.slideModuleData[index] || []), ...incoming];
        this.slideModuleTotalCount[index] = result.totalCount || 0;
      },
      () => {
        this.slideModuleLoading[index] = false;
      },
    );
  }

  getModuleLabel(module: string): string {
    const map: any = {
      plan: 'Plan',
      testSery: 'Test Series',
      editorial: 'Editorial',
      currentAffair: 'Current Affair',
      jobAlert: 'Job Alert',
    };
    return map[module] || 'Item';
  }

  /**
   * Returns the display label for an item.
   * currentAffair and editorial expose `title`; plan and testSery use `name`.
   */
  getModuleItemLabel(module: string, item: any): string {
    switch (module) {
      case 'currentAffair':
        return item.title || item.name || item._id;
      case 'editorial':
        return item.title || item.name || item._id;
      case 'jobAlert':
        return item.title || item.name || item._id;
      default:
        return item.name || item._id;
    }
  }

  private cleanupSlideModuleState(index: number): void {
    delete this.slideModuleData[index];
    delete this.slideModuleLoading[index];
    delete this.slideModuleTotalCount[index];
    delete this.slideModulePageIndex[index];
    if (this.slideModuleSearch[index]) {
      this.slideModuleSearch[index].complete();
      delete this.slideModuleSearch[index];
    }
  }

  getBanners(): void {
    this.bannerData = [];
    this.httpRequestService.request('get', 'banners').subscribe(
      (result: any) => {
        const data = result.data;
        this.bannerData = data.sort(
          (a: any, b: any) => a.position - b.position,
        );
        this.slideFormArray.clear();
        data.forEach((element: any) => {
          this.slideFormArray.push(this.createSlide(element));
        });
        if (!this.slideFormArray.length) {
          this.addSlides();
        }

        // Pre-fetch module items for slides that already have a saved module
        this.slideFormArray.controls.forEach((control, i) => {
          const module = control.get('module')?.value;
          this.updateReferenceIdValidators(module, i);
          if (module && module !== 'freeMock') {
            this.initSlideModuleSearch(i);
            this.fetchModuleData(i, '', 0);
          }
        });
      },
      (error: any) => {},
    );
  }

  customRequestHeaders = () => {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
  };

  drop(event: CdkDragDrop<AbstractControl[]>): void {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    if (previousIndex !== currentIndex) {
      const formArray = this.slideFormArray;
      const movedItem = formArray.at(previousIndex);
      formArray.removeAt(previousIndex);
      formArray.insert(currentIndex, movedItem);
      formArray.controls.forEach((control, index) => {
        control.patchValue({ position: index + 1 });
      });
      this.reindexModuleState(previousIndex, currentIndex);
    }
  }

  private reindexModuleState(from: number, to: number): void {
    const savedData = this.slideModuleData[from];
    const savedLoading = this.slideModuleLoading[from];
    const savedTotal = this.slideModuleTotalCount[from];
    const savedPage = this.slideModulePageIndex[from];
    const savedSearch = this.slideModuleSearch[from];

    if (from < to) {
      for (let i = from; i < to; i++) {
        this.slideModuleData[i] = this.slideModuleData[i + 1];
        this.slideModuleLoading[i] = this.slideModuleLoading[i + 1];
        this.slideModuleTotalCount[i] = this.slideModuleTotalCount[i + 1];
        this.slideModulePageIndex[i] = this.slideModulePageIndex[i + 1];
        this.slideModuleSearch[i] = this.slideModuleSearch[i + 1];
      }
    } else {
      for (let i = from; i > to; i--) {
        this.slideModuleData[i] = this.slideModuleData[i - 1];
        this.slideModuleLoading[i] = this.slideModuleLoading[i - 1];
        this.slideModuleTotalCount[i] = this.slideModuleTotalCount[i - 1];
        this.slideModulePageIndex[i] = this.slideModulePageIndex[i - 1];
        this.slideModuleSearch[i] = this.slideModuleSearch[i - 1];
      }
    }

    this.slideModuleData[to] = savedData;
    this.slideModuleLoading[to] = savedLoading;
    this.slideModuleTotalCount[to] = savedTotal;
    this.slideModulePageIndex[to] = savedPage;
    this.slideModuleSearch[to] = savedSearch;
  }

  submit(): void {
    if (!this.addUpdateBannerForm.valid) {
      this.markFormGroupTouched(this.addUpdateBannerForm);
    } else {
      if (this.idForUpdate) {
        this.addOrUpdateBanners(
          'put',
          `banners/${this.idForUpdate}`,
          'Banners Successfully Updated',
        );
      } else {
        this.addOrUpdateBanners('post', 'banners', 'Banner Added Successfully');
      }
    }
  }

  addOrUpdateBanners(
    requestMethod: string,
    requestURL: string,
    successMessage: string,
  ): void {
    this.buttonLoading = true;
    const finalData = this.addUpdateBannerForm.value.slides;
    this.httpRequestService
      .request(requestMethod, requestURL, finalData)
      .subscribe(
        (result: any) => {
          this.notificationService.success('', successMessage);
          this.buttonLoading = false;
          this.getBanners();
        },
        (error: any) => {
          if (error.error.errors) {
            const allErrors: string[] = Object.values(error.error.errors);
            for (const err of allErrors) {
              this.notificationService.error('', err);
            }
          } else {
            this.notificationService.error('', error.error.message);
          }
          this.buttonLoading = false;
        },
      );
  }

  UploadImageFile(
    info: { file: NzUploadFile },
    FormControl: string,
    index: number,
  ) {
    switch (info.file.status) {
      case 'uploading':
        if (FormControl === 'imageForMobile') {
          this.thumbImageLoading = true;
        } else {
          this.imageLoading = true;
        }
        break;
      case 'done':
        if (FormControl === 'imageForMobile') {
          this.slideFormArray.controls[index].patchValue({
            imageForMobile: info.file.response.data.path,
          });
          this.thumbImageLoading = false;
        } else {
          this.imageLoading = false;
          this.slideFormArray.controls[index].patchValue({
            image: info.file.response.data.path,
          });
        }
        break;
      case 'error':
        this.msg.error('Network error');
        if (FormControl === 'imageForMobile') {
          this.thumbImageLoading = false;
        } else {
          this.imageLoading = false;
        }
        break;
    }
  }

  beforeRelatedMediaUpload = (
    file: NzUploadFile,
    fileList: NzUploadFile[],
  ): Observable<any> => {
    return new Observable((observer: Observer<boolean>) => {
      const isImage =
        file.type === 'image/jpg' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/JPG' ||
        file.type === 'image/JPEG' ||
        file.type === 'image/PNG' ||
        file.type === 'image/webp';
      if (!isImage) {
        this.msg.error('You can only upload image file!');
        observer.complete();
        return;
      }
      const isLt20M = file.size ? file.size / 1024 / 1024 < 20 : false;
      if (!isLt20M) {
        this.msg.error('Image must be smaller than 20MB!');
        observer.complete();
        return;
      }
      observer.next(isImage && isLt20M);
      observer.complete();
    });
  };

  removeImage(path: string, index: number): void {
    let params: any = { path };
    this.slideFormArray.controls[index].patchValue({ image: null });
    this.httpRequestService
      .request('post', 'media/delete-media', params)
      .subscribe((result: any) => {
        if (result) {
          this.notificationService.success('', 'Image Deleted Successfully');
        }
      });
  }

  removeThumbImage(path: string, index: number): void {
    let params: any = { path };
    this.slideFormArray.controls[index].patchValue({ imageForMobile: null });
    this.httpRequestService
      .request('post', 'media/delete-media', params)
      .subscribe((result: any) => {
        if (result && result.data) {
          this.notificationService.success('', 'Image Deleted Successfully');
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const i in formGroup.controls) {
      if (formGroup.controls.hasOwnProperty(i)) {
        formGroup.controls[i].markAsDirty();
        formGroup.controls[i].updateValueAndValidity();

        if (formGroup.controls[i] instanceof FormGroup) {
          this.markFormGroupTouched(formGroup.controls[i] as FormGroup);
        }

        if (formGroup.controls[i] instanceof FormArray) {
          const formArray = formGroup.controls[i] as FormArray;
          for (const j in formArray.controls) {
            if (formArray.controls.hasOwnProperty(j)) {
              formArray.controls[j].markAsDirty();
              formArray.controls[j].updateValueAndValidity();
              if (formArray.controls[j] instanceof FormGroup) {
                this.markFormGroupTouched(formArray.controls[j] as FormGroup);
              }
            }
          }
        }
      }
    }
  }
}
